import { useLayoutEffect, useRef, useState } from 'react';
import styles from './SegmentedControl.module.css';

interface SegmentedControlProps<T extends string> {
  options: Array<{ value: T; label: string }>;
  selectedValue: T;
  onValueChange: (value: T) => void;
  testId?: string;
}

export default function SegmentedControl<T extends string>({
  options,
  selectedValue,
  onValueChange,
  testId,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (options.length <= 1) return null;

  const segmentWidth = containerWidth / options.length;
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === selectedValue)
  );

  return (
    <div
      ref={containerRef}
      className={styles.container}
      role="tablist"
      data-testid={testId}
    >
      {containerWidth > 0 && (
        <div
          className={styles.pill}
          style={{
            width: segmentWidth,
            transform: `translateX(${selectedIndex * segmentWidth}px)`,
          }}
        />
      )}

      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={styles.segment}
          role="tab"
          aria-selected={option.value === selectedValue}
          data-testid={testId ? `${testId}-${option.value}` : undefined}
          onClick={() => onValueChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
